import type { ApiContentTag, ApiExternalFramework, GetContentBundleResponse } from '@/types/api'

export const MAX_FRAMEWORK_FILTERS = 5
const FRAMEWORK_COLOR_COUNT = 8
const CONTEXT_COLOR_COUNT = 8
const CONTEXT_ORDER_OFFSET = 100

export type FilterChipKind = 'framework' | 'context'

export interface FilterChipModel {
  key: string
  kind: FilterChipKind
  label: string
  title: string
  /** Shown inside a marker: the selection slot for frameworks, a letter for contexts. */
  glyph: string
  /** Fill colour. */
  color: string
  /** Text and border colour for context chips; framework chips use white text and border. */
  ink?: string
  /** Frameworks sort before contexts; within each, selection or tag order. */
  order: number
}

/**
 * Glyph follows the 1-based selection slot, unique because at most 5 frameworks are selected.
 * Colour follows the framework's position by id, so it never shifts with the selection.
 */
export function frameworkChip(
  framework: ApiExternalFramework,
  selectedFrameworkIds: number[],
  allFrameworks: ApiExternalFramework[],
): FilterChipModel {
  const slot = selectedFrameworkIds.indexOf(framework.id) + 1
  const index = allFrameworks.map((fw) => fw.id).sort((a, b) => a - b).indexOf(framework.id)
  return {
    key: `fw-${framework.id}`,
    kind: 'framework',
    label: framework.shortCode,
    title: framework.name,
    glyph: String(slot),
    color: `var(--color-fw-chip-${(Math.max(index, 0) % FRAMEWORK_COLOR_COUNT) + 1})`,
    order: slot,
  }
}

/** Glyph and colour follow the tag's position in the full tag list, so they never shift with the selection. */
export function contextChip(tag: ApiContentTag, allTags: ApiContentTag[]): FilterChipModel {
  const index = [...allTags].sort((a, b) => a.sortOrder - b.sortOrder).findIndex((t) => t.id === tag.id)
  const name = tag.displayName ?? tag.name
  return {
    key: `ctx-${tag.id}`,
    kind: 'context',
    label: name,
    title: name,
    glyph: String.fromCharCode(65 + (index % 26)),
    color: `var(--color-ctx-chip-${(index % CONTEXT_COLOR_COUNT) + 1}-bg)`,
    ink: `var(--color-ctx-chip-${(index % CONTEXT_COLOR_COUNT) + 1}-ink)`,
    order: CONTEXT_ORDER_OFFSET + index,
  }
}

/** Chips for every active framework and context filter, in chip order. Null when neither filter is active. */
export function buildActiveFilterChips(
  bundle: GetContentBundleResponse,
  selectedFrameworkIds: number[],
  selectedContentTagIds: number[],
): FilterChipModel[] | null {
  const allFrameworks = bundle.externalFrameworks ?? []
  const frameworks = allFrameworks.filter((fw) => selectedFrameworkIds.includes(fw.id))
  // Slots are taken from visible frameworks only, so a persisted id the user can no longer see shifts nothing.
  const visibleIds = selectedFrameworkIds.filter((id) => frameworks.some((fw) => fw.id === id))
  const tags = selectedContentTagIds.includes(-1)
    ? []
    : bundle.contentTags.filter((tag) => selectedContentTagIds.includes(tag.id))
  if (frameworks.length === 0 && tags.length === 0) return null

  return [
    ...frameworks.map((fw) => frameworkChip(fw, visibleIds, allFrameworks)),
    ...tags.map((tag) => contextChip(tag, bundle.contentTags)),
  ].sort((a, b) => a.order - b.order)
}

/**
 * Chips for every practice that matches an active framework or context filter, minus `hiddenKeys`.
 * Returns null when neither filter is active. Practices are never hidden by this.
 */
export function buildPracticeFilterChips(
  bundle: GetContentBundleResponse,
  selectedFrameworkIds: number[],
  selectedContentTagIds: number[],
  hiddenKeys: string[] = [],
): Map<number, FilterChipModel[]> | null {
  const active = buildActiveFilterChips(bundle, selectedFrameworkIds, selectedContentTagIds)
  if (!active) return null

  const chipByKey = new Map(active.filter((c) => !hiddenKeys.includes(c.key)).map((c) => [c.key, c]))

  const keysByPractice = new Map<number, Set<string>>()
  const add = (practiceId: number, key: string) => {
    if (!chipByKey.has(key)) return
    const keys = keysByPractice.get(practiceId)
    if (keys) keys.add(key)
    else keysByPractice.set(practiceId, new Set([key]))
  }
  for (const m of bundle.practiceCrosswalkMappings ?? []) add(m.practiceId, `fw-${m.externalFrameworkId}`)
  for (const cc of bundle.contextualContent) {
    if (cc.practiceId !== null) add(cc.practiceId, `ctx-${cc.contentTagId}`)
  }

  const out = new Map<number, FilterChipModel[]>()
  for (const [practiceId, keys] of keysByPractice) {
    out.set(practiceId, [...keys].map((k) => chipByKey.get(k)!).sort((a, b) => a.order - b.order))
  }
  return out
}

/** Distinct union of chip lists, in chip order. */
export function mergeChips(lists: (FilterChipModel[] | undefined)[]): FilterChipModel[] {
  const byKey = new Map<string, FilterChipModel>()
  for (const list of lists) for (const chip of list ?? []) byKey.set(chip.key, chip)
  return [...byKey.values()].sort((a, b) => a.order - b.order)
}
